import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const BulkOperationsPanel = ({ 
  selectedItems = [], 
  onBulkAction, 
  onClearSelection,
  isVisible = false 
}) => {
  const [activeOperation, setActiveOperation] = useState(null);
  const [operationData, setOperationData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const bulkOperations = [
    {
      id: 'assign-role',
      label: 'Assign Role',
      icon: 'UserCog',
      description: 'Change role for selected users',
      requiresInput: true,
      inputType: 'select',
      options: [
        { value: 'Field Inspector', label: 'Field Inspector' },
        { value: 'Senior Inspector', label: 'Senior Inspector' },
        { value: 'Manager', label: 'Manager' },
        { value: 'QC Specialist', label: 'QC Specialist' },
        { value: 'Account Manager', label: 'Account Manager' },
        { value: 'Freelance Inspector', label: 'Freelance Inspector' }
      ]
    },
    {
      id: 'update-status',
      label: 'Update Status',
      icon: 'RefreshCw',
      description: 'Change account status for selected users',
      requiresInput: true,
      inputType: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'suspended', label: 'Suspended' }
      ]
    },
    {
      id: 'enable-mfa',
      label: 'Enable MFA',
      icon: 'Shield',
      description: 'Enable multi-factor authentication',
      requiresInput: false
    },
    {
      id: 'disable-mfa',
      label: 'Disable MFA',
      icon: 'ShieldOff',
      description: 'Disable multi-factor authentication',
      requiresInput: false
    },
    {
      id: 'assign-tenant',
      label: 'Assign Tenant',
      icon: 'Building2',
      description: 'Assign users to a tenant organization',
      requiresInput: true,
      inputType: 'select',
      options: [
        { value: 'Qualitech Inspections', label: 'Qualitech Inspections' },
        { value: 'SafetyFirst Solutions', label: 'SafetyFirst Solutions' },
        { value: 'Independent Inspectors', label: 'Independent Inspectors' }
      ]
    },
    {
      id: 'send-notification',
      label: 'Send Notification',
      icon: 'Mail',
      description: 'Send email notification to selected users',
      requiresInput: true,
      inputType: 'textarea'
    },
    {
      id: 'export-data',
      label: 'Export Data',
      icon: 'Download',
      description: 'Export user data to CSV',
      requiresInput: false
    },
    {
      id: 'reset-password',
      label: 'Reset Password',
      icon: 'Key',
      description: 'Send password reset emails',
      requiresInput: false,
      confirmRequired: true
    }
  ];

  const handleOperationSelect = (operation) => {
    setActiveOperation(operation);
    setOperationData({});
  };

  const handleExecute = async () => {
    if (!activeOperation) return;

    setIsProcessing(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      onBulkAction(activeOperation?.id, selectedItems, operationData);
      setActiveOperation(null);
      setOperationData({});
    } catch (error) {
      console.error('Bulk operation failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setActiveOperation(null);
    setOperationData({});
  };

  const renderOperationInput = () => {
    if (!activeOperation?.requiresInput) return null;

    switch (activeOperation?.inputType) {
      case 'select':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Select {activeOperation?.label?.toLowerCase()}:
            </label>
            <select
              value={operationData?.value || ''}
              onChange={(e) => setOperationData({ value: e?.target?.value })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select an option...</option>
              {activeOperation?.options?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Message:</label>
            <textarea
              value={operationData?.message || ''}
              onChange={(e) => setOperationData({ message: e?.target?.value })}
              placeholder="Enter your message..."
              rows={4}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (!isVisible || selectedItems?.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-300">
      <div className="bg-card border border-border rounded-lg shadow-elevated p-4 min-w-96 max-w-2xl">
        {!activeOperation ? (
          <>
            {/* Selection Summary */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {selectedItems?.length}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {selectedItems?.length} user{selectedItems?.length > 1 ? 's' : ''} selected
                  </p>
                  <p className="text-xs text-muted-foreground">Choose a bulk operation</p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={onClearSelection}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Operations Grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {bulkOperations?.map((operation) => (
                <Button
                  key={operation?.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleOperationSelect(operation)}
                  iconName={operation?.icon}
                  iconPosition="left"
                  iconSize={14}
                  className="justify-start h-auto py-3"
                >
                  <div className="text-left">
                    <div className="font-medium">{operation?.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {operation?.description}
                    </div>
                  </div>
                </Button>
              ))}
            </div>

            {/* Selected Users Preview */}
            <div className="border-t border-border pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Selected Users:</p>
              <div className="flex flex-wrap gap-1">
                {selectedItems?.slice(0, 5)?.map((user) => (
                  <span
                    key={user?.id}
                    className="inline-flex items-center px-2 py-1 bg-muted rounded-md text-xs text-foreground"
                  >
                    {user?.name}
                  </span>
                ))}
                {selectedItems?.length > 5 && (
                  <span className="inline-flex items-center px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                    +{selectedItems?.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Operation Details */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Icon name={activeOperation?.icon} size={20} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{activeOperation?.label}</p>
                  <p className="text-xs text-muted-foreground">{activeOperation?.description}</p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Operation Input */}
            {renderOperationInput()}

            {/* Confirmation */}
            {activeOperation?.confirmRequired && (
              <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Confirmation Required</p>
                    <p className="text-xs text-warning/80 mt-1">
                      This action cannot be undone. Please confirm you want to proceed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Affects {selectedItems?.length} user{selectedItems?.length > 1 ? 's' : ''}
              </p>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleExecute}
                  loading={isProcessing}
                  disabled={activeOperation?.requiresInput && !operationData?.value && !operationData?.message}
                >
                  {isProcessing ? 'Processing...' : 'Execute'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkOperationsPanel;